import { observer } from 'mobx-react';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Box } from '@mui/material';
import convertSchemaToColumns from './lib/convertSchemaToColumns';
import { useLocalization } from '../../providers/LocalizationProvider';
import filterDateHandler from '../../helpers/filterDateHandler';
import { useStore } from '../../providers/MobxProvider';
import LoggerUtil from '../../helpers/LoggerUtil';
import { useSnackbar } from '../../providers/SnackbarProvider';
import SnackbarApiErrorMessage from '../UI/SnackbarApiErrorMessage';

export function buildSortParam({
  sortingState = [],
  sortBy = '',
  sortOrder = '',
}) {
  if (Array.isArray(sortingState) && sortingState.length > 0) {
    if (sortingState[0]?.id) {
      return {
        sortBy: sortingState[0]?.id,
        sortOrder: sortingState[0]?.desc ? 'DESC' : 'ASC',
      };
    }
    return null;
  }

  if (sortBy) {
    return {
      sortBy,
      sortOrder: sortOrder ?? 'ASC',
    };
  }
  return null;
}

const FormListSemiAdvanced = forwardRef(
  (
    {
      columnPrimaryId,
      schema,
      dataProvider,
      initialState,
      listApiUrl,
      accessorFunctions = {},
      onRowCountChange = () => undefined,
      accessorFunctionsAdditional = {},
      children = null,
      onApiResponse = () => undefined,
      stickyColumn = false,
      storeName,
      responseFormatter = null,
      sortOrder = '',
      sortBy = '',
      rowsPerPageOptions = null,
    },
    ref,
  ) => {
    const { formatMessage } = useLocalization();
    const store = useStore();
    const snackbar = useSnackbar();

    if (!store.uiState?.[storeName]) {
      store.addToStore({ storeName, storeType: 'LIST_V1' });
    }

    const currentStore = store.uiState?.[storeName];

    const {
      paginationState,
      filters = [],
      sortingState = [],
    } = currentStore.get(storeName);

    const { get } = dataProvider;

    // data and fetching state
    const [tableData, setTableData] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);

    // table state
    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    const currentAPIUrl = useRef(new URLSearchParams());

    const columns = useMemo(() =>
      convertSchemaToColumns(
        schema,
        formatMessage,
        accessorFunctions,
        accessorFunctionsAdditional,
        stickyColumn,
      ),
    );

    async function fetchData() {
      if (!tableData.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      const apiUrl = new URL(listApiUrl);

      apiUrl.searchParams.set('page', `${paginationState.pageIndex + 1 || 1}`);
      apiUrl.searchParams.set('limit', `${paginationState?.pageSize || 10}`);

      if (Array.isArray(filters) && filters.length > 0) {
        filters.every(filter => {
          let newFilter = filter;

          // filter?.vaddTimeToDate to be used for DynamicDatePicker
          if (filter?.addTimeToDate) {
            newFilter = filterDateHandler(filter);
          }

          // filter?.value?.operator to be used for DynamicDatePicker
          apiUrl.searchParams.set(
            `${newFilter?.searchAs || newFilter?.field}`,
            `$${
              newFilter?.value?.operator === 'eqNull'
                ? 'eq'
                : newFilter?.value?.operator || newFilter?.operator
            }:${
              newFilter?.value?.operator === 'eqNull'
                ? null
                : newFilter?.value?.value || newFilter?.value
            }`,
          );
          return true;
        });
        currentAPIUrl.current = apiUrl.searchParams;
      }

      const sort = buildSortParam({ sortingState, sortBy, sortOrder });
      if (sort) {
        apiUrl.searchParams.set('sortBy', sort.sortBy);
        apiUrl.searchParams.set('sortOrder', sort.sortOrder);
      }

      try {
        const apiResponse = await get(apiUrl.href);
        onApiResponse(apiResponse);
        if (apiResponse?.ok) {
          const response = apiResponse.data;
          let tableDataResponse = response.data;
          if (responseFormatter) {
            tableDataResponse = responseFormatter(tableDataResponse);
          }
          setTableData(tableDataResponse);
          // TODO: Remove this hack once API has a standard way of sending total
          const tempRowCount =
            response?.total || response?.totalItems || response?.data.length;
          setRowCount(tempRowCount);
          setPageSize(response?.limit || 10);
        } else {
          snackbar.showMessage({
            type: 'error',
            data: <SnackbarApiErrorMessage errors={apiResponse?.errors} />,
          });
        }
      } catch (error) {
        setIsError(true);
        LoggerUtil.log('List API Error', error);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    }

    useEffect(() => {
      fetchData();
    }, [
      JSON.stringify(paginationState),
      JSON.stringify(filters),
      JSON.stringify(sortingState),
    ]);

    useEffect(() => {
      onRowCountChange(rowCount);
    }, [rowCount]);

    const table = useMaterialReactTable({
      data: tableData,
      autoResetPageIndex: false,
      localization: store.domain.locale.getForMuiTable(),
      columns,
      getRowId: row => row[columnPrimaryId],
      rowCount,
      pageSize,
      manualFiltering: true,
      enableTopToolbar: false,
      enableBottomToolbar: true,
      manualPagination: true,
      manualSorting: true,
      enableColumnFilters: false,
      enableSorting: true,
      enableFilters: false,
      initialState,
      enableColumnActions: false,
      muiTablePaperProps: { elevation: 0 },
      ...(rowsPerPageOptions !== null && {
        muiPaginationProps: {
          rowsPerPageOptions,
        },
      }),
      state: {
        globalFilter: '',
        isLoading,
        showAlertBanner: isError,
        showProgressBars: isRefetching,
        sorting: sortingState,
        pagination: paginationState,
      },
      muiToolbarAlertBannerProps: isError
        ? {
            color: 'error',
            children: formatMessage({
              id: 'ERROR_LOADING_DATA',
              defaultMessage: 'Error loading data',
            }),
          }
        : undefined,
      onPaginationChange: updater => {
        // TODO: remove after version upgrade
        const newPagination =
          typeof updater === 'function' ? updater(paginationState) : updater;
        currentStore.set(storeName, {
          paginationState: newPagination,
          sortingState,
          filters,
        });
      },
      onSortingChange: updater => {
        // TODO: remove after version upgrade
        const newSorting =
          typeof updater === 'function' ? updater(sortingState) : updater;

        currentStore.set(storeName, {
          paginationState: { pageIndex: 0, pageSize },
          sortingState: newSorting,
          filters,
        });
      },
      muiBottomToolbarProps: {
        sx: {
          minHeight: '2.5em',
        },
      },
    });

    useImperativeHandle(ref, () => ({
      getFilter: () => currentAPIUrl.current,
      getVisibleColumns: () => table.getVisibleLeafColumns(),
      reset: table.resetColumnVisibility,
      setCompact: () => table.setDensity('compact'),
    }));

    return (
      <Box ref={ref}>
        <MaterialReactTable table={table} />
        {children}
      </Box>
    );
  },
);

export default observer(FormListSemiAdvanced);
